import {
  ArrowBackIosNew,
  ArrowDownward,
  ArrowForwardIos,
  ArrowUpward,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { ReactElement, useEffect, useRef, useState } from "react";
import { COLORS } from "../../../assets/theme";
import {
  IAction,
  IField,
  IPageable,
  IPageInput,
  ISort,
  Params,
} from "../../../types/table/interfaces.type";
import DataTableBody from "./DataTableBody";
import CustomCheckbox from "../../CustomCheckbox";
import { UseQueryResult } from "@tanstack/react-query";
import {
  getObjectDiffMask,
  getObjectSetDiffMask,
} from "../../../utils/diffUtils";
import useDragScroll from "../../../utils/useDragScroll";

type FetchFuncType<T> =
  | ((params: Params<T>) => UseQueryResult<IPageable<T>, Error>)
  | ((id: string) => UseQueryResult<IPageable<T>, Error>)
  | ((params: Params<T>) => Promise<IPageable<T>>)
  | ((id: string) => Promise<T>);

function callFetchFunc<T>(
  fetchFunc: FetchFuncType<T>,
  params: Params<T>,
  id?: string,
): any {
  if (id && fetchFunc.length === 1) {
    return (fetchFunc as (id: string) => any)(id);
  }
  return (fetchFunc as (params: Params<T>) => any)(params);
}

interface IPage extends IPageInput {
  totalPages: number;
  totalElements: number;
}

interface IPaginationRowProps {
  pageInput: IPageInput;
  setPageInput: (input: IPageInput | ((old: IPageInput) => IPageInput)) => void;
  page: IPage;
}

interface IPaginatedTableProps<T> {
  fields: IField<T>[];
  filters: Params<T>;
  fetchFunc: FetchFuncType<T>;
  actions: IAction<T>[];
  renderActions?: (row: T) => React.ReactElement[];
  shadeRow?: (row: T) => boolean;
  shadeBg?: string | ((row: T) => string | undefined);
  shadeColor?: string;
  checkboxSelect?: boolean;
  onRowSelect?: (row: T, checked: boolean) => void;
  isRowSelected?: (row: T) => boolean;
  sortableFields?: (keyof T)[];
  highlightDiff?: boolean;
  /** "adjacent" compares each row with the one below it (history view),
   *  "all" flags every column that is not identical across all the rows. */
  diffScope?: "adjacent" | "all";
  onRowClick?: (row: T) => void;
  /** Sort applied on first render, before the user clicks a column header. */
  initialSort?: ISort<T>;
}

function PaginatedTable<T>({
  fields,
  filters,
  fetchFunc,
  actions,
  checkboxSelect,
  onRowSelect,
  isRowSelected,
  renderActions,
  shadeRow,
  shadeBg,
  shadeColor,
  sortableFields,
  highlightDiff = false,
  diffScope = "adjacent",
  onRowClick,
  initialSort,
}: IPaginatedTableProps<T>) {
  const dragScrollRef = useDragScroll<HTMLDivElement>();
  const [sort, setSort] = useState<ISort<T>>(initialSort ?? {});
  const [pageInput, setPageInput] = useState<IPageInput>({
    page: 0,
    size: 20,
  });

  // Reset during render (not in an effect) so the old page is never fetched with the new filters.
  const filtersKey = JSON.stringify(filters);
  const [prevFiltersKey, setPrevFiltersKey] = useState(filtersKey);
  const filtersChanged = filtersKey !== prevFiltersKey;
  if (filtersChanged) {
    setPrevFiltersKey(filtersKey);
    setPageInput((prev) => ({ ...prev, page: 0 }));
  }

  const queryParams: Params<T> = {
    ...filters,
    page: filtersChanged ? 0 : pageInput.page,
    size: pageInput.size,
    sort: [sort.field, sort.direction].filter(Boolean).join(","),
  };

  const [manualState, setManualState] = useState<{
    data?: IPageable<T>;
    isPending: boolean;
    error?: any;
  }>({ isPending: false });
  const [manualKey, setManualKey] = useState(0);

  const queryParamsKey = JSON.stringify(queryParams);
  const isManualRef = useRef<boolean | null>(null);
  const manualCallRef = useRef<{ key: string; promise: any } | null>(null);

  let result: any;
  let isManual: boolean;

  if (
    isManualRef.current === true &&
    manualCallRef.current?.key === queryParamsKey
  ) {
    isManual = true;
    result = manualCallRef.current.promise;
  } else {
    try {
      result = callFetchFunc(fetchFunc, queryParams);
    } catch (e) {
      result = Promise.reject(e);
    }
    isManual = !!result && typeof result.then === "function";
    isManualRef.current = isManual;
    if (isManual) {
      manualCallRef.current = { key: queryParamsKey, promise: result };
    }
  }

  useEffect(() => {
    if (isManual) {
      setManualState({ isPending: true });
      result
        .then((data: IPageable<T>) =>
          setManualState({ data, isPending: false }),
        )
        .catch((error: any) => setManualState({ error, isPending: false }));
    }
  }, [manualKey, queryParamsKey]);

  const finalResult = isManual
    ? {
        data: manualState.data,
        isPending: manualState.isPending,
        error: manualState.error,
      }
    : result;

  const isReactQueryResult =
    finalResult &&
    typeof finalResult === "object" &&
    "data" in finalResult &&
    "isPending" in finalResult &&
    "error" in finalResult;

  if (!isReactQueryResult) {
    return (
      <Box>
        <TableContainer
          sx={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                {checkboxSelect && <TableCell sx={{ width: 50 }} />}
                {fields.map((f) => (
                  <TableCell key={String(f.key)} align="center">
                    {f.header}
                  </TableCell>
                ))}
                {actions.length ? (
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    Actions
                  </TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={fields.length + (actions.length ? 1 : 0)}
                  align="center"
                  sx={{ height: 100, backgroundColor: "#f9f9f9", color: "red" }}
                >
                  <Typography variant="h6">
                    Error: fetchFunc did not return a valid result
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  const { data: pageable, isPending, error } = finalResult;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (
      pageable &&
      Array.isArray(pageable.content) &&
      pageable.content.length === 0 &&
      pageInput.page > 0
    ) {
      setPageInput((prev) => ({ ...prev, page: prev.page - 1 }));
    }
  }, [pageable, pageInput.page]);

  const handleSort = (key: keyof T) => {
    setSort((prevSort) => {
      const newDirection =
        prevSort.field === key && prevSort.direction === "desc"
          ? "asc"
          : "desc";
      return { field: key, direction: newDirection };
    });
    setPageInput((prev) => ({ ...prev, page: 0 }));
  };

  const getSortSymbol = (key: keyof T): ReactElement | undefined => {
    if (sort.field !== key) return undefined;
    return sort.direction === "asc" ? (
      <ArrowUpward sx={{ fontSize: 15 }} />
    ) : (
      <ArrowDownward sx={{ fontSize: 15 }} />
    );
  };
  const contentRows = Array.isArray(pageable?.content) ? pageable.content : [];
  let highlights: Record<string, boolean>[] = [];
  if (highlightDiff && diffScope === "all") {
    const setMask = getObjectSetDiffMask(contentRows as any[]);
    highlights = contentRows.map(() => setMask);
  } else if (highlightDiff) {
    highlights = contentRows.map((elem: any, idx: number, list: any[]) => {
      const ref = list[idx + 1] ?? elem;
      return getObjectDiffMask(elem, ref);
    });
  }

  const allSelected =
    checkboxSelect && isRowSelected
      ? contentRows.every((row: T) => isRowSelected(row))
      : false;
  const handleSelectAllToggle = () => {
    if (onRowSelect) {
      contentRows.forEach((row: T) => onRowSelect(row, !allSelected));
    }
  };
  return (
    <Box>
      <TableContainer
        ref={dragScrollRef}
        sx={{
          backgroundColor: "#fff",
          borderRadius: "10px 10px 0 0",
          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          maxHeight: { xs: "calc(100vh - 170px)", sm: "75vh" },
          "@supports (height: 100dvh)": {
            maxHeight: { xs: "calc(100dvh - 170px)", sm: "75vh" },
          },
          "& .MuiTableCell-stickyHeader": { backgroundColor: "#fff" },
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {checkboxSelect && (
                <TableCell sx={{ width: 50 }}>
                  <CustomCheckbox
                    checked={allSelected}
                    onChange={handleSelectAllToggle}
                    ariaLabel="select all rows"
                  />
                </TableCell>
              )}
              {fields.map((f) => {
                const isSortable = sortableFields?.includes(f.key);
                return (
                  <TableCell
                    key={String(f.key)}
                    onClick={isSortable ? () => handleSort(f.key) : undefined}
                    align="center"
                    sx={{
                      cursor: isSortable ? "pointer" : "default",
                      fontWeight: "600",
                      color: COLORS.typographyColor,
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{ width: "max-content", margin: "0 auto" }}
                      gap={1}
                    >
                      {f.header}
                      {isSortable && getSortSymbol(f.key)}
                    </Box>
                  </TableCell>
                );
              })}
              {actions.length ? (
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  Actions
                </TableCell>
              ) : null}
            </TableRow>
          </TableHead>

          {error ? (
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={fields.length + (actions.length ? 1 : 0)}
                  align="center"
                  sx={{ height: 100, backgroundColor: "#f9f9f9", color: "red" }}
                >
                  <Typography variant="h6">Error: No data fetched</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          ) : isPending ? (
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={fields.length + (actions.length ? 1 : 0)}
                  align="center"
                  sx={{ height: 100, backgroundColor: "#f9f9f9" }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <CircularProgress
                      size={50}
                      thickness={5}
                      sx={{ color: COLORS.GOLD }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <DataTableBody<T>
              fields={fields}
              content={pageable ? pageable.content : []}
              actions={actions}
              renderActions={renderActions}
              shadeRow={shadeRow}
              shadeBg={shadeBg}
              shadeColor={shadeColor}
              checkboxSelect={checkboxSelect}
              onRowSelect={onRowSelect}
              isRowSelected={isRowSelected}
              highlights={highlights}
              onRowClick={onRowClick}
            />
          )}
        </Table>
      </TableContainer>
      {!isPending && pageable && pageable.totalPages > 0 && (
        <Box
          sx={{
            alignItems: "center",
            borderRadius: "0 0  10px 10px",
            padding: { xs: "4px 8px", sm: "8px 16px" },
            backgroundColor: "#fff",
          }}
        >
          <PaginationRow
            pageInput={pageInput}
            setPageInput={setPageInput}
            page={{
              page: pageable.number,
              size: pageable.size,
              totalElements: pageable.totalElements,
              totalPages: pageable.totalPages,
            }}
          />
        </Box>
      )}
    </Box>
  );
}

function PaginationRow({ pageInput, setPageInput, page }: IPaginationRowProps) {
  const currentPage = pageInput.page ?? 0;
  const totalPages = page.totalPages || 1;
  const totalElements = page.totalElements;

  const [pageInputValue, setPageInputValue] = useState<string>(
    String(currentPage + 1),
  );

  useEffect(() => {
    setPageInputValue(String(currentPage + 1));
  }, [currentPage]);

  const handlePageInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setPageInputValue(value);
    }
  };

  const handlePageInputKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter") {
      handlePageInputSubmit();
    }
  };

  const handlePageInputBlur = () => {
    handlePageInputSubmit();
  };

  const handlePageInputSubmit = () => {
    const inputNumber = parseInt(pageInputValue, 10);

    if (isNaN(inputNumber) || inputNumber < 1) {
      setPageInputValue(String(currentPage + 1));
      return;
    }

    const targetPage = Math.min(Math.max(inputNumber - 1, 0), totalPages - 1);

    if (targetPage !== currentPage) {
      setPageInput((prev) => ({
        ...prev,
        page: targetPage,
      }));
    }

    setPageInputValue(String(targetPage + 1));
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      flexWrap="wrap"
      gap={{ xs: 1, sm: 2 }}
      ml={{ xs: 0, sm: 1 }}
      mr={{ xs: 0, sm: 1 }}
      sx={{ width: "100%" }}
    >
      <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 2 }}>
        <IconButton
          disabled={currentPage <= 0}
          onClick={() =>
            setPageInput((prev) => ({
              ...prev,
              page: Math.max(prev.page - 1, 0),
            }))
          }
          sx={{
            color: currentPage > 0 ? COLORS.placeHolderColor : "#ccc",
            fontSize: { xs: "16px", sm: "18px" },
            p: { xs: 0.5, sm: 1 },
          }}
        >
          <ArrowBackIosNew fontSize="small" />
        </IconButton>

        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "13px", sm: "16px" },
            color: COLORS.placeHolderColor,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "13px", sm: "16px" },
              fontWeight: "bold",
              color: COLORS.placeHolderColor,
            }}
          >
            Page
          </Typography>
          <TextField
            value={pageInputValue}
            onChange={handlePageInputChange}
            onKeyPress={handlePageInputKeyPress}
            onBlur={handlePageInputBlur}
            size="small"
            sx={{
              width: { xs: "40px", sm: "50px" },
              "& .MuiInputBase-root": {
                height: { xs: "24px", sm: "28px" },
                fontSize: { xs: "12px", sm: "14px" },
              },
              "& .MuiInputBase-input": {
                textAlign: "center",
                padding: { xs: "2px 4px", sm: "4px 6px" },
                fontWeight: "bold",
                color: COLORS.placeHolderColor,
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#e0e0e0",
                },
                "&:hover fieldset": {
                  borderColor: COLORS.placeHolderColor,
                },
                "&.Mui-focused fieldset": {
                  borderColor: COLORS.placeHolderColor,
                  borderWidth: "1px",
                },
              },
            }}
            inputProps={{
              min: 1,
              max: totalPages,
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
          />
          <Typography
            sx={{
              fontSize: { xs: "13px", sm: "16px" },
              fontWeight: "bold",
              color: COLORS.placeHolderColor,
            }}
          >
            of {totalPages}
          </Typography>
        </Box>

        <IconButton
          disabled={currentPage + 1 >= totalPages}
          onClick={() =>
            setPageInput((prev) => ({
              ...prev,
              page: Math.min(prev.page + 1, totalPages - 1),
            }))
          }
          sx={{
            color:
              currentPage + 1 < totalPages ? COLORS.placeHolderColor : "#ccc",
            fontSize: { xs: "16px", sm: "18px" },
            p: { xs: 0.5, sm: 1 },
          }}
        >
          <ArrowForwardIos fontSize="small" />
        </IconButton>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        gap={{ xs: 0.5, sm: 1 }}
        sx={{ flexShrink: 0 }}
      >
        <Box
          sx={{
            fontSize: { xs: "11px", sm: "0.875rem" },
            color: COLORS.placeHolderColor,
            display: { xs: "none", sm: "block" },
          }}
        >
          Rows per page:
        </Box>
        <Box
          sx={{
            fontSize: "11px",
            color: COLORS.placeHolderColor,
            display: { xs: "block", sm: "none" },
          }}
        >
          Rows:
        </Box>
        <Select
          value={pageInput.size}
          onChange={(e) =>
            setPageInput((prev) => ({
              ...prev,
              size: Number(e.target.value),
              page: 0,
            }))
          }
          sx={{
            height: { xs: "24px", sm: "32px" },
            fontSize: { xs: "11px", sm: "0.875rem" },
            width: { xs: "58px", sm: "80px" },
            minWidth: { xs: "58px", sm: "80px" },
            "& .MuiSelect-select": {
              padding: { xs: "2px 4px", sm: "4px 8px" },
            },
          }}
        >
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={20}>20</MenuItem>
          <MenuItem value={50}>50</MenuItem>
          <MenuItem value={100}>100</MenuItem>
        </Select>
      </Box>
      <Typography
        sx={{
          fontSize: { xs: "11px", sm: "0.875rem" },
          color: COLORS.placeHolderColor,
          ml: "auto",
          whiteSpace: "nowrap",
        }}
      >
        Total elements: {totalElements}
      </Typography>
    </Box>
  );
}

export default PaginatedTable;
